import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api', withCredentials: true });

const PHASE = { IDLE: 'idle', ANALYZING: 'analyzing', ANALYZED: 'analyzed', RUNNING: 'running', DONE: 'done', ERROR: 'error' };

function LogBox({ logs }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [logs]);
  return (
    <div ref={ref} style={{
      background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
      padding: 12, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)',
      height: 200, overflowY: 'auto', marginTop: 12,
    }}>
      {logs.length === 0
        ? <span style={{ color: 'var(--text-muted)' }}>Waiting...</span>
        : logs.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  );
}

export default function PodcastMatcher() {
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [clientName, setClientName] = useState('');
  const [file, setFile] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState(new Set());
  const [wordCount, setWordCount] = useState(0);
  const [logs, setLogs] = useState([]);
  const [doneResult, setDoneResult] = useState(null);
  const [error, setError] = useState('');
  const [showRecover, setShowRecover] = useState(false);
  const [recoverForm, setRecoverForm] = useState({ analysisBatchId: '', pitchBatchId: '', clientName: '', categories: '' });

  const pollRef = useRef(null);
  const fileRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPolling(), []);

  const poll = useCallback((id) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/podcast-matcher/status/${id}`);
        const job = res.data;
        setLogs(job.logs || []);
        if (job.status === 'done' && job.result) {
          stopPolling();
          setDoneResult(job.result);
          setPhase(PHASE.DONE);
        } else if (job.status === 'error') {
          stopPolling();
          setError(job.error || 'An error occurred.');
          setPhase(PHASE.ERROR);
        }
      } catch (_) {}
    }, 2000);
  }, []);

  const toggleCat = (cat) => {
    setSelectedCats(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const toggleAllCats = () => {
    setSelectedCats(prev => prev.size === allCategories.length ? new Set() : new Set(allCategories));
  };

  const handleAnalyze = async () => {
    if (!file || !clientName) return;
    setPhase(PHASE.ANALYZING);
    setError('');

    const form = new FormData();
    form.append('package', file);
    form.append('clientName', clientName);

    try {
      const res = await api.post('/podcast-matcher/analyze', form);
      const { sessionId: sid, suggestedCategories, allCategories: cats, wordCount: wc } = res.data;
      setSessionId(sid);
      setAllCategories(cats || []);
      setSelectedCats(new Set(suggestedCategories || []));
      setWordCount(wc || 0);
      setPhase(PHASE.ANALYZED);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase(PHASE.ERROR);
    }
  };

  const handleRun = async () => {
    setPhase(PHASE.RUNNING);
    setLogs([]);
    setError('');
    setDoneResult(null);

    const form = new FormData();
    form.append('clientName', clientName);
    form.append('categories', Array.from(selectedCats).join(','));
    if (sessionId) form.append('sessionId', sessionId);

    try {
      const res = await api.post('/podcast-matcher/run', form);
      poll(res.data.jobId);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase(PHASE.ERROR);
    }
  };

  const reset = () => {
    setPhase(PHASE.IDLE);
    setFile(null);
    setSessionId(null);
    setAllCategories([]);
    setSelectedCats(new Set());
    setWordCount(0);
    setDoneResult(null);
    setError('');
    setLogs([]);
  };

  const handleRecover = async () => {
    const { analysisBatchId, pitchBatchId, clientName: rcName, categories } = recoverForm;
    if (!analysisBatchId || !pitchBatchId || !rcName) return;
    setPhase(PHASE.RUNNING);
    setLogs([]);
    setError('');
    setDoneResult(null);
    try {
      const res = await api.post('/podcast-matcher/recover', { analysisBatchId, pitchBatchId, clientName: rcName, categories });
      poll(res.data.jobId);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setPhase(PHASE.ERROR);
    }
  };

  const s = { card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 } };

  return (
    <div style={{ padding: 32, maxWidth: 860, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, marginBottom: 6 }}>Podcast Matcher</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          Upload a client package, select categories to narrow the pool, and the tool will score every podcast for fit, generate personalized pitches, and save everything to a new tab in your master sheet.
        </p>
      </div>

      {/* ── Step 1: Upload ── */}
      {phase === PHASE.IDLE && (
        <div style={s.card}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Client Name</label>
              <input
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder="e.g. Dr. Jane Smith"
                style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text-primary)', fontSize: 13 }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Client Package</label>
              <input ref={fileRef} type="file" accept=".txt,.pdf,.docx,.md,.csv" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0] || null)} />
              <button className="btn btn-ghost" onClick={() => fileRef.current.click()} style={{ marginRight: 12 }}>
                {file ? file.name : 'Choose file...'}
              </button>
              {file && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{(file.size / 1024).toFixed(1)} KB</span>}
            </div>

            <button className="btn btn-primary" disabled={!file || !clientName} onClick={handleAnalyze} style={{ alignSelf: 'flex-start' }}>
              Analyze Package
            </button>
          </div>
        </div>
      )}

      {/* ── Analyzing ── */}
      {phase === PHASE.ANALYZING && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontWeight: 500 }}>Reading client package and identifying relevant categories...</span>
          </div>
        </div>
      )}

      {/* ── Step 2: Categories + Run ── */}
      {phase === PHASE.ANALYZED && (
        <div style={s.card}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 500, marginBottom: 2 }}>{clientName}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{file?.name} · {wordCount.toLocaleString()} words</div>
              </div>
              <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={reset}>Change file</button>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Categories{selectedCats.size > 0 ? ` (${selectedCats.size} selected)` : ' — all'}
                </label>
                {allCategories.length > 0 && (
                  <button className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }} onClick={toggleAllCats}>
                    {selectedCats.size === allCategories.length ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>
              {selectedCats.size > 0 && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                  Pre-selected based on the client package — adjust as needed.
                </p>
              )}

              {allCategories.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No categories found in master sheet.</div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {allCategories.map(cat => {
                    const active = selectedCats.has(cat);
                    return (
                      <button key={cat} onClick={() => toggleCat(cat)} style={{
                        padding: '5px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer',
                        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                        background: active ? 'var(--accent-soft)' : 'transparent',
                        color: active ? 'var(--accent)' : 'var(--text-secondary)',
                        fontWeight: active ? 500 : 400, transition: 'all 0.15s',
                      }}>
                        {cat}
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedCats.size === 0 && allCategories.length > 0 && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>No categories selected — all active podcasts will be analyzed.</p>
              )}
            </div>

            <button className="btn btn-primary" onClick={handleRun} style={{ alignSelf: 'flex-start' }}>
              Run Matcher
            </button>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {phase === PHASE.ERROR && (
        <div style={s.card}>
          <div style={{ color: 'var(--red)', fontSize: 13, background: 'var(--red-soft)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: 16 }}>
            {error}
          </div>
          <button className="btn btn-ghost" onClick={reset}>Try again</button>
        </div>
      )}

      {/* ── Running ── */}
      {phase === PHASE.RUNNING && (
        <div style={s.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontWeight: 500 }}>Running...</span>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Analyzing podcasts, generating pitches, and saving to your master sheet. This may take a few minutes.</p>
          <LogBox logs={logs} />
        </div>
      )}

      {/* ── Done ── */}
      {phase === PHASE.DONE && (
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✓</div>
            <h2 style={{ fontSize: 18, marginBottom: 8 }}>Done!</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 6 }}>
              {doneResult?.matchCount} matches saved to your master sheet.
            </p>
            {doneResult?.sheetTab && (
              <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 20 }}>
                Tab: <strong style={{ color: 'var(--text-primary)' }}>{doneResult.sheetTab}</strong>
              </p>
            )}
            <button className="btn btn-primary" onClick={reset}>Run another</button>
          </div>
        </div>
      )}

      {/* ── Recover from batches ── */}
      {phase === PHASE.IDLE && (
        <div style={{ marginTop: 24 }}>
          <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--text-muted)' }} onClick={() => setShowRecover(v => !v)}>
            {showRecover ? 'Hide' : 'Recover results from existing batches'}
          </button>
          {showRecover && (
            <div style={{ ...s.card, marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                If a previous run completed its Anthropic batches but failed to save to the sheet, paste the batch IDs from your Anthropic console here.
              </p>
              {[
                { key: 'analysisBatchId', label: 'Analysis Batch ID' },
                { key: 'pitchBatchId', label: 'Pitch Batch ID' },
                { key: 'clientName', label: 'Client Name' },
                { key: 'categories', label: 'Categories (comma-separated, optional)' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>{label}</label>
                  <input
                    value={recoverForm[key]}
                    onChange={e => setRecoverForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', color: 'var(--text-primary)', fontSize: 13 }}
                  />
                </div>
              ))}
              <button
                className="btn btn-primary"
                style={{ alignSelf: 'flex-start' }}
                disabled={!recoverForm.analysisBatchId || !recoverForm.pitchBatchId || !recoverForm.clientName}
                onClick={handleRecover}
              >
                Save to Sheet
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
